/* eslint-disable camelcase */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import SemanticForm, { FormCustomItem, FormStyleAttributes } from '../../Types/SemanticForm';
import { SemanticFormS } from '../restapi/semanticFormService';
import { ToastS } from '../ToastS';

interface BookitupFormContextInterface {
  bookitupForm: SemanticForm | null;
  bookitupFormStyles: FormStyleAttributes;
  loading: boolean;
  handleChange: (patch: Partial<SemanticForm>) => unknown;
  updateForm: (patch: Partial<SemanticForm>) => unknown;
  updateFormStyles: (patch: Partial<FormStyleAttributes>) => unknown;
  addCustomItem: (item: FormCustomItem) => unknown;
  builderKey: string;
  toggleHChaptcha: () => void;
  addingHChaptcha: boolean;
}

export const BookitupFormContext = createContext<BookitupFormContextInterface>({} as BookitupFormContextInterface);

export const BookitupFormContextProvider = () => {
  const { id: formId } = useParams();
  const [loading, setLoading] = useState(false);
  const [addingHChaptcha, setAddingHChaptcha] = useState(false);
  const [bookitupForm, setBookitupForm] = useState<SemanticForm | null>(null);
  const [bookitupFormStyles, setBookitupFormStyles] = useState<FormStyleAttributes>({} as FormStyleAttributes);
  const [builderKey, setBuilderKey] = useState(uuidv4());
  const { task_data } = bookitupForm ?? { task_data: [] };

  useEffect(() => {
    if (formId) {
      setLoading(true);
      SemanticFormS.getForm(formId)
        .then((resJSON) => {
          if (resJSON) {
            const { styleAttributes } = resJSON;
            setBookitupForm(resJSON);
            setBookitupFormStyles(styleAttributes);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [formId]);

  const updateForm = async (patch: Partial<SemanticForm>) => {
    if (formId) {
      const patchedForm = await SemanticFormS.patch(formId, patch);
      if (patchedForm) {
        setBookitupForm(patchedForm);
        if (patch.formStyle) {
          const { styleAttributes } = patchedForm;
          setBookitupFormStyles(styleAttributes);
        }
      }
    }
  };

  const handleChange = (patch: Partial<SemanticForm>) => {
    setBookitupForm((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
  };

  const updateFormStyles = async (patch: Partial<FormStyleAttributes>) => {
    if (formId) {
      const success = await SemanticFormS.patchStyles(formId, patch);
      if (success) {
        setBookitupFormStyles((prev) => ({ ...prev, ...patch }));
        handleChange({ outOfSync: true });
      }
    }
  };

  const addCustomItem = async (item: FormCustomItem) => {
    if (formId) {
      const form = await SemanticFormS.addCustomItem(formId, item);
      if (form) {
        setBookitupForm(form);
        setBuilderKey(uuidv4());
      }
    }
  };

  const toggleHChaptcha = async () => {
    setAddingHChaptcha(true);
    let activating = false;
    const index = task_data.findIndex((item: any) => item.field_name?.startsWith('HCaptcha_'));
    let nextTaskData: any;

    if (index !== -1) {
      // remove existing hCaptcha field
      nextTaskData = task_data.filter((_, i) => i !== index);
    } else {
      // add hCaptcha field
      activating = true;
      nextTaskData = [...task_data, SemanticFormS.HCAPTCHA_FIELD];
    }

    await updateForm({
      ...bookitupForm,
      task_data: nextTaskData,
    });
    setAddingHChaptcha(false);
    ToastS.info('HCaptcha-report', `HCaptcha ${activating ? 'aktiviert' : 'deaktiviert'}`);
  };

  const value = useMemo(
    () => ({
      bookitupForm,
      bookitupFormStyles,
      loading,
      builderKey,
      addingHChaptcha,
      handleChange,
      updateForm,
      updateFormStyles,
      addCustomItem,
      toggleHChaptcha,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookitupForm, loading, bookitupFormStyles, builderKey, addingHChaptcha],
  );

  return (
    <BookitupFormContext.Provider value={value}>
      <Outlet />
    </BookitupFormContext.Provider>
  );
};

export const useFormMetadata = () => {
  const { bookitupForm } = useContext(BookitupFormContext);
  // eslint-disable-next-line camelcase
  const { task_data } = bookitupForm ?? { task_data: [] };
  return SemanticFormS.resolveFormMetadata(task_data);
};
